/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Team Workload — a dedicated workspace dashboard for "who does what work, how
 * often, and what kind of job", across the whole unit.
 *
 *  - Bar chart: each member's task-allocation count (or earnings), sorted so the
 *    busiest read top-to-bottom — compare everyone at a glance.
 *  - Job matrix: member × job-category counts — what kind of work each person does.
 *  - Compare: pick 2+ members → stacked bars contrasting their job MIX head-to-head.
 *  - Every member is clickable (social-media style) → their profile + portfolio.
 *
 * Counts come from the shared `computePersonWork` helper, so they reconcile with the
 * Portfolio Overview panel and the Dashboard's workload views.
 */
import { useState, useEffect, useRef } from 'react';
import { useData } from '../context/AppDataContext';
import { useProfileViewer } from '../components/ProfileViewer';
import { Avatar, Ico } from '../components/ui';
import { downloadCsv, dateStamp } from '../lib/exportUtils';
import { computePersonWork, orderCategories, personCategoryBreakdown } from '../lib/workload';

const thb = (v: number) => '฿' + Math.round(v).toLocaleString();
const isClosed = (p: any) => p.status === 'Completed' || p.status === 'Terminated';

/** Subsequence fuzzy match — `q` (already lower-cased) loosely found inside `s`. */
const fuzzyHit = (q: string, s: string): boolean => {
  if (!q) return true;
  const nm = s.toLowerCase();
  if (nm.includes(q)) return true;
  let i = 0;
  for (const ch of nm) { if (ch === q[i]) i++; if (i === q.length) return true; }
  return false;
};

export function WorkloadPage({ openProject }: { openProject: (id: string, tab?: string) => void }) {
  const D = useData();
  const { open: openProfile } = useProfileViewer();
  const [metric, setMetric] = useState<'tasks' | 'earned'>('tasks');
  const [view, setView] = useState<'bars' | 'matrix'>('bars');
  const [activeOnly, setActiveOnly] = useState(false);
  const [showIdle, setShowIdle] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Deck-card expander: which matrix cell (member × category) is open, anchored to its rect.
  const [expand, setExpand] = useState<null | { u: any; cat: string; x: number; yt: number; yb: number }>(null);
  const [q, setQ] = useState('');
  const popRef = useRef<HTMLDivElement>(null);

  const toneOf = (cat: string) => (D.categories[cat] || { tone: 'slate' }).tone;
  const projectsById: Record<string, any> = {};
  D.projects.forEach((p) => { projectsById[p.id] = p; });

  const openCell = (e: any, u: any, cat: string) => {
    const r = e.currentTarget.getBoundingClientRect();
    setQ('');
    setExpand({ u, cat, x: r.left, yt: r.top, yb: r.bottom });
  };

  // Close the expander on Escape, on resize, and on any scroll that isn't inside it
  // (a fixed popover can't follow the cell once the table/page scrolls away).
  useEffect(() => {
    if (!expand) return;
    const close = (ev?: Event) => {
      if (ev && popRef.current && ev.target instanceof Node && popRef.current.contains(ev.target)) return;
      setExpand(null);
    };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setExpand(null); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [expand]);

  // Scope to active (non-closed) projects when asked; else everything.
  const projectIds = activeOnly
    ? new Set(D.projects.filter((p) => !isClosed(p)).map((p) => p.id))
    : null;
  const { rows } = computePersonWork({ timeline: D.timeline, compensations: D.compensations, team: D.team, projectIds });

  // Active = holds any work or earnings in scope. Idle members are hidden unless asked.
  const active = rows.filter((r) => r.tasks > 0 || r.earned > 0);
  const idleCount = rows.length - active.length;
  const visible = showIdle ? rows : active;

  // Fairness numbers (over active members — see the Portfolio panel for the same choice).
  const totalTasks = rows.reduce((s, r) => s + r.tasks, 0);
  const totalEarned = rows.reduce((s, r) => s + r.earned, 0);
  const avgTasks = active.length ? totalTasks / active.length : 0;
  const busiest = [...rows].sort((a, b) => b.tasks - a.tasks)[0];
  const lowCut = avgTasks * 0.6;
  const highCut = avgTasks * 1.4;
  const loadTone = (n: number): { tone: string; label: string } | null => {
    if (avgTasks <= 0 || n <= 0) return null;
    if (n < lowCut) return { tone: 'amber', label: 'Light' };
    if (n > highCut) return { tone: 'red', label: 'Heavy' };
    return null;
  };

  const valOf = (r: any) => (metric === 'tasks' ? r.tasks : r.earned);
  const maxVal = Math.max(1, ...visible.map(valOf));
  const barRows = [...visible].sort((a, b) => valOf(b) - valOf(a) || a.u.name.localeCompare(b.u.name));

  // Job-category columns actually used by the visible rows (matrix + legend).
  const usedCats: Record<string, number> = {};
  visible.forEach((r) => Object.entries(r.cats).forEach(([c, n]) => { usedCats[c] = (usedCats[c] || 0) + n; }));
  const activeCats = orderCategories(D.categories, usedCats);
  const matrixRows = [...visible].sort((a, b) => b.tasks - a.tasks || a.u.name.localeCompare(b.u.name));

  // Compare picker → selected members' job mix as stacked bars.
  const toggleSelect = (id: string) => setSelected((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  // Only compare members still active in the current scope — a member can drop to idle
  // when "Active projects only" is ticked after they were picked; don't render a 0-bar.
  const selRows = rows.filter((r) => selected.has(r.u.id) && (r.tasks > 0 || r.earned > 0));
  const selCats = (() => {
    const used: Record<string, number> = {};
    selRows.forEach((r) => Object.entries(r.cats).forEach(([c, n]) => { used[c] = (used[c] || 0) + n; }));
    return orderCategories(D.categories, used);
  })();
  const selMaxTotal = Math.max(1, ...selRows.map((r) => r.tasks));

  const exportCsv = () => {
    if (!barRows.length) return;
    downloadCsv(barRows, {
      Member: (r: any) => r.u.name,
      Role: (r: any) => r.u.role,
      'Task allocations': (r: any) => r.tasks,
      Projects: (r: any) => r.projects.size,
      'Top jobs': (r: any) => Object.entries(r.cats).sort((a: any, b: any) => b[1] - a[1]).slice(0, 3).map(([c, n]) => `${c} x${n}`).join('; '),
      'Earned (฿)': (r: any) => r.earned,
      'Received (฿)': (r: any) => r.received,
      'Outstanding (฿)': (r: any) => r.outstanding,
    }, `team-workload-${dateStamp()}.csv`);
  };

  const segBtn = (on: boolean) => ({
    height: 28, fontSize: 12, padding: '0 12px',
    background: on ? 'var(--accent)' : 'var(--surface-alt)',
    color: on ? '#fff' : 'var(--text-2)',
    border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontWeight: on ? 700 : 500,
  }) as const;

  const Member = ({ u, sub }: { u: any; sub?: string }) => (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', minWidth: 0 }}
      onClick={() => openProfile(u.id)}
      title="View profile & portfolio"
    >
      <Avatar user={u} size="sm" />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</span>
        {sub && <span style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{sub}</span>}
      </span>
    </span>
  );

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>Team Workload</h2>
          <div className="sub">
            Who does what work, how often, and what kind of job — {active.length} active of {D.team.length} members
            {activeOnly ? ' · active projects only' : ' · all projects'}. Click anyone to open their profile.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={exportCsv} disabled={!barRows.length}><Ico.dl /> Export CSV</button>
        </div>
      </div>

      {/* Controls */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-b" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 0 }}>
            <button style={{ ...segBtn(view === 'bars'), borderRadius: '6px 0 0 6px' }} onClick={() => setView('bars')}>Bar chart</button>
            <button style={{ ...segBtn(view === 'matrix'), borderRadius: '0 6px 6px 0', borderLeft: 'none' }} onClick={() => setView('matrix')}>Job matrix</button>
          </div>
          {view === 'bars' && (
            <div style={{ display: 'flex', gap: 0 }}>
              <button style={{ ...segBtn(metric === 'tasks'), borderRadius: '6px 0 0 6px' }} onClick={() => setMetric('tasks')}>by tasks</button>
              <button style={{ ...segBtn(metric === 'earned'), borderRadius: '0 6px 6px 0', borderLeft: 'none' }} onClick={() => setMetric('earned')}>by earnings</button>
            </div>
          )}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} /> Active projects only
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={showIdle} onChange={(e) => setShowIdle(e.target.checked)} /> Show idle members{idleCount ? ` (${idleCount})` : ''}
          </label>
        </div>
      </div>

      {/* Fairness strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
        <div className="stat"><div className="label">Total allocations</div><div className="v tabular">{totalTasks}</div><div className="d">across {active.length} active member{active.length === 1 ? '' : 's'}</div></div>
        <div className="stat"><div className="label">Avg per person</div><div className="v tabular">{avgTasks.toFixed(1)}</div><div className="d">tasks per active member</div></div>
        <div className="stat"><div className="label">Busiest</div><div className="v" style={{ fontSize: 16 }}>{busiest && busiest.tasks ? busiest.u.name : '—'}</div><div className="d">{busiest && busiest.tasks ? `${busiest.tasks} task${busiest.tasks === 1 ? '' : 's'}` : 'no work yet'}</div></div>
        <div className="stat" style={{ borderColor: 'var(--accent)' }}><div className="label">Total earned</div><div className="v" style={{ color: 'var(--accent)' }}>{thb(totalEarned)}</div><div className="d">compensation in scope</div></div>
      </div>

      {/* Compare picker */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-h"><h3>Compare members</h3><span className="sub">{selected.size ? `${selected.size} selected` : 'pick 2 or more to compare their job mix'}</span></div>
        <div className="card-b" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {active.map((r) => {
            const on = selected.has(r.u.id);
            return (
              <button key={r.u.id} onClick={() => toggleSelect(r.u.id)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px 3px 4px', borderRadius: 999, cursor: 'pointer', fontSize: 12, fontWeight: on ? 700 : 500, border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'), background: on ? 'var(--accent)' : 'var(--surface-alt)', color: on ? '#fff' : 'var(--text-2)' }}>
                <Avatar user={r.u} size="sm" /> {r.u.name} <span className="tabular" style={{ opacity: 0.85 }}>· {r.tasks}</span>
              </button>
            );
          })}
          {!active.length && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No members with work in scope.</span>}
        </div>
        {selRows.length >= 2 && (
          <div className="card-b" style={{ borderTop: '1px solid var(--border)' }}>
            {/* Legend */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
              {selCats.map((c) => (
                <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: `var(--t-${toneOf(c)}-fg)`, display: 'inline-block' }} /> {c}
                </span>
              ))}
            </div>
            {/* Stacked job-mix bar per selected member */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...selRows].sort((a, b) => b.tasks - a.tasks).map((r) => (
                <div key={r.u.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 150, flexShrink: 0 }}><Member u={r.u} sub={r.u.role} /></div>
                  <div style={{ flex: 1, display: 'flex', height: 22, borderRadius: 4, overflow: 'hidden', background: 'var(--surface-alt)' }} title={`${r.u.name}: ${r.tasks} task${r.tasks === 1 ? '' : 's'}`}>
                    {selCats.map((c) => {
                      const n = r.cats[c] || 0;
                      if (!n) return null;
                      const w = (n / selMaxTotal) * 100;
                      return <div key={c} style={{ width: `${w}%`, background: `var(--t-${toneOf(c)}-fg)`, opacity: 0.9 }} title={`${c}: ${n}`} />;
                    })}
                  </div>
                  <span className="tabular" style={{ width: 32, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>{r.tasks}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bar chart OR job matrix */}
      {view === 'bars' ? (
        <div className="card">
          <div className="card-h"><h3>{metric === 'tasks' ? 'Task allocations per member' : 'Earnings per member'}</h3><div className="sub">Sorted busiest-first · click a member for their profile</div></div>
          <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {barRows.map((r) => {
              const v = valOf(r);
              const pct = (v / maxVal) * 100;
              const flag = loadTone(r.tasks);
              const barColor = metric === 'earned' ? 'var(--t-green-fg)' : (flag ? `var(--t-${flag.tone}-fg)` : 'var(--accent)');
              return (
                <div key={r.u.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 170, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Member u={r.u} />
                    {metric === 'tasks' && flag && <span className="chip" data-tone={flag.tone} style={{ fontSize: 9.5, padding: '1px 6px' }}><span className="dot" />{flag.label}</span>}
                  </div>
                  <div style={{ flex: 1, position: 'relative', height: 20, background: 'var(--surface-alt)', borderRadius: 4, overflow: 'hidden', cursor: 'pointer' }}
                    title={`${r.u.name}: ${r.tasks} task${r.tasks === 1 ? '' : 's'} · ${r.projects.size} project${r.projects.size === 1 ? '' : 's'} · ${thb(r.earned)} earned`}
                    onClick={() => openProfile(r.u.id)}>
                    <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, minWidth: v ? 2 : 0, background: barColor, borderRadius: 4, opacity: 0.85, transition: 'width 0.2s' }} />
                  </div>
                  <span className="tabular" style={{ width: 84, flexShrink: 0, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>
                    {metric === 'tasks' ? v : thb(v)}
                  </span>
                </div>
              );
            })}
            {!barRows.length && <div style={{ color: 'var(--text-3)', fontSize: 12 }}>No members with work in scope. {idleCount > 0 && !showIdle ? 'Tick "Show idle members" to list everyone.' : ''}</div>}
          </div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <div className="card-h"><h3>What job each member does</h3><div className="sub">Each cell: <b>N(major+minor)</b> over <b>Σ%</b> — N tasks, of which they <i>led</i> (top share) vs assisted, and the summed %responsibility · click a cell to expand by project · click a name for the profile</div></div>
          <div className="card-b flush">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 10px', position: 'sticky', left: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', minWidth: 170, zIndex: 1 }}>Team member</th>
                  {activeCats.map((c) => (
                    <th key={c} title={c} className="num" style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)', color: `var(--t-${toneOf(c)}-fg)`, fontWeight: 700, whiteSpace: 'nowrap', maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</th>
                  ))}
                  <th className="num" style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', fontWeight: 700 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {matrixRows.map((r) => {
                  const stats = Object.values(r.catStats);
                  const tMajor = stats.reduce((s, x) => s + x.major, 0);
                  const tMinor = stats.reduce((s, x) => s + x.minor, 0);
                  const tSum = stats.reduce((s, x) => s + x.sumPct, 0);
                  return (
                  <tr key={r.u.id}>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1 }}>
                      <Member u={r.u} sub={r.u.role} />
                    </td>
                    {activeCats.map((c) => {
                      const n = r.cats[c] || 0;
                      const st = r.catStats[c];
                      const isOpen = expand?.u.id === r.u.id && expand?.cat === c;
                      return (
                        <td key={c} className="num tabular" onClick={n ? (e) => openCell(e, r.u, c) : undefined} style={{
                          padding: '6px', borderBottom: '1px solid var(--border)',
                          color: n ? `var(--t-${toneOf(c)}-fg)` : 'var(--text-3)',
                          background: isOpen ? `var(--t-${toneOf(c)}-fg)` : (n ? `var(--t-${toneOf(c)}-bg)` : undefined),
                          fontWeight: n ? 700 : 400, opacity: n ? 1 : 0.5,
                          cursor: n ? 'pointer' : 'default',
                          ...(isOpen ? { color: 'var(--surface)', outline: `2px solid var(--t-${toneOf(c)}-fg)` } : {}),
                        }} title={n ? `${r.u.name} · ${c}: ${n} task${n === 1 ? '' : 's'} (${st?.major ?? 0} major + ${st?.minor ?? 0} minor) · Σ ${st?.sumPct ?? 0}% responsibility · click to expand by project` : `${r.u.name} · ${c}: none`}>
                          {n ? (
                            <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.18 }}>
                              <span style={{ borderBottom: '1px dashed currentColor', whiteSpace: 'nowrap' }}>{n}<span style={{ fontSize: 9, fontWeight: 400, opacity: 0.7 }}>({st?.major ?? 0}+{st?.minor ?? 0})</span></span>
                              <span style={{ fontSize: 9, fontWeight: 400, opacity: 0.62, whiteSpace: 'nowrap' }}>{st?.sumPct ?? 0}%</span>
                            </span>
                          ) : '·'}
                        </td>
                      );
                    })}
                    <td className="num tabular" style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', fontWeight: 700 }}
                      title={`${r.tasks} task${r.tasks === 1 ? '' : 's'} · ${tMajor} major + ${tMinor} minor · Σ ${tSum}% responsibility`}>
                      {r.tasks ? (
                        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.18 }}>
                          <span style={{ whiteSpace: 'nowrap' }}>{r.tasks}<span style={{ fontSize: 9, fontWeight: 400, opacity: 0.7 }}>({tMajor}+{tMinor})</span></span>
                          <span style={{ fontSize: 9, fontWeight: 400, opacity: 0.62, whiteSpace: 'nowrap' }}>{tSum}%</span>
                        </span>
                      ) : 0}
                    </td>
                  </tr>
                  );
                })}
                <tr>
                  <td style={{ padding: '8px 10px', borderTop: '2px solid var(--border)', position: 'sticky', left: 0, background: 'var(--surface-alt)', fontWeight: 700, zIndex: 1 }}>Total</td>
                  {activeCats.map((c) => (
                    <td key={c} className="num tabular" style={{ padding: '8px 6px', borderTop: '2px solid var(--border)', background: 'var(--surface-alt)', fontWeight: 700, color: `var(--t-${toneOf(c)}-fg)` }}>{usedCats[c] || 0}</td>
                  ))}
                  <td className="num tabular" style={{ padding: '8px 10px', borderTop: '2px solid var(--border)', borderLeft: '1px solid var(--border)', background: 'var(--surface-alt)', fontWeight: 800 }}>{visible.reduce((s, r) => s + r.tasks, 0)}</td>
                </tr>
                {!matrixRows.length && <tr><td colSpan={activeCats.length + 2} style={{ padding: 16, color: 'var(--text-3)', fontSize: 12 }}>No members with work in scope.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Deck-card expander — click a matrix N to see which projects it spans and the
          member's %responsibility on each. Fuzzy-searchable for when projects pile up. */}
      {expand && (() => {
        const tone = toneOf(expand.cat);
        const detail = personCategoryBreakdown(D.timeline, expand.u.id, expand.cat, projectIds)
          .map((d) => ({ ...d, p: projectsById[d.projectId] }))
          .sort((a, b) => b.count - a.count || String(a.p?.code || a.projectId).localeCompare(String(b.p?.code || b.projectId)));
        const cellN = detail.reduce((s, d) => s + d.count, 0);
        const cellMajor = detail.reduce((s, d) => s + d.tasks.filter((t) => t.major).length, 0);
        const cellSumPct = detail.reduce((s, d) => s + d.tasks.reduce((a, t) => a + t.pct, 0), 0);
        const ql = q.trim().toLowerCase();
        const shown = detail.filter((d) => fuzzyHit(ql, `${d.p?.code || ''} ${d.p?.shortTitle || ''} ${d.p?.title || ''}`));
        // "Major" = led the task (top share); else assisted (minor). Green pill = major.
        const majTag = (major: boolean) => (
          <span style={{ fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', padding: '0 4px', borderRadius: 3, whiteSpace: 'nowrap', color: major ? 'var(--t-green-fg)' : 'var(--text-3)', background: major ? 'var(--t-green-bg)' : 'transparent', border: major ? '1px solid transparent' : '1px solid var(--border)' }}>{major ? 'major' : 'minor'}</span>
        );

        // Anchor to the cell; flip above when there's no room below, clamp to viewport.
        const W = 340;
        const left = Math.max(8, Math.min(expand.x, window.innerWidth - W - 8));
        const spaceBelow = window.innerHeight - expand.yb;
        const above = spaceBelow < 240 && expand.yt > spaceBelow;
        const pos: any = above
          ? { left, bottom: window.innerHeight - expand.yt + 6, maxHeight: Math.min(420, expand.yt - 16) }
          : { left, top: expand.yb + 6, maxHeight: Math.min(420, spaceBelow - 16) };

        return (
          <>
            <div onClick={() => setExpand(null)} style={{ position: 'fixed', inset: 0, zIndex: 60 }} />
            <div ref={popRef} style={{
              position: 'fixed', ...pos, width: W, zIndex: 61, background: 'var(--surface)',
              border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{ padding: '9px 11px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <Avatar user={expand.u} size="sm" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{expand.u.name}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>
                    <span style={{ color: `var(--t-${tone}-fg)`, fontWeight: 700 }}>{expand.cat}</span>
                    {' · '}{cellN} task{cellN === 1 ? '' : 's'} · {detail.length} project{detail.length === 1 ? '' : 's'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>
                    {cellN}({cellMajor}+{cellN - cellMajor}) · {cellMajor} major / {cellN - cellMajor} minor · Σ {cellSumPct}% work
                  </div>
                </div>
                <button className="btn ghost icon sm" onClick={() => setExpand(null)} title="Close"><Ico.close /></button>
              </div>

              {/* Fuzzy search — appears once there are enough projects to be worth filtering. */}
              {detail.length > 3 && (
                <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                  <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter projects…"
                    style={{ width: '100%', height: 28, fontSize: 12, padding: '0 8px', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', outline: 'none' }} />
                </div>
              )}

              {/* Deck of project cards */}
              <div style={{ overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {shown.map((d) => {
                  const single = d.tasks.length === 1;
                  return (
                    <div key={d.projectId}
                      onClick={() => { setExpand(null); openProject(d.projectId, 'timeline'); }}
                      title="Open project timeline"
                      style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderLeft: `3px solid var(--t-${tone}-fg)`, borderRadius: 6, padding: '7px 9px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>{d.p?.code || '—'}</span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 11, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.p?.shortTitle || d.p?.title || d.projectId}</span>
                        {single
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>{majTag(d.tasks[0].major)}<span className="tabular" style={{ fontWeight: 800, fontSize: 12, color: `var(--t-${tone}-fg)` }}>{d.tasks[0].pct}%</span></span>
                          : <span className="chip" data-tone={tone} style={{ fontSize: 9.5, padding: '1px 6px' }}>{d.count} tasks</span>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {d.tasks.map((t) => (
                          <div key={t.id} style={{ display: 'flex', gap: 6, fontSize: 11, alignItems: 'baseline' }}>
                            <span style={{ flex: 1, minWidth: 0, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
                            {!single && <>{majTag(t.major)}<span className="tabular" style={{ color: 'var(--text-3)', fontWeight: 700 }}>{t.pct}%</span></>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {!shown.length && <div style={{ padding: 12, fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>No projects match “{q}”.</div>}
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}
